import React, { useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Link } from '@planne-software/uni';
import { IconInfoCircle } from '@planne-software/uni/icons';
import { Input } from '@planne-software/uni/Input';

import { useTranslate } from '@src/core/hooks';

import { authLinkSx } from '../../styles';
import { FieldWithLinkStack, InputHintRow, InputHintText, ZipCodeInputWrapper } from '../styles';
import type { SignupFormValues } from '../hooks/useSignupForm/signupSchema';
import { MaskedField } from '@src/common/ui/MaskedField';
import { ZipCodeHelpModal } from './ZipCodeHelpModal';

type AddressFieldsProps = {
  control: Control<SignupFormValues>;
  isForeign: boolean;
  disabled?: boolean;
};

export const AddressFields = (props: AddressFieldsProps) => {
  const { control, isForeign, disabled } = props;
  const { i18n } = useTranslate();
  const [helpOpen, setHelpOpen] = useState(false);

  if (isForeign) {
    return (
      <Controller
        name='zipCode'
        control={control}
        render={({ field: { onChange, onBlur, value, name, ref }, fieldState }) => (
          <ZipCodeInputWrapper>
            <Input
              type='text'
              name={name}
              id={name}
              label={i18n('checkout_auth_signup_postal_code_label')}
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={disabled}
              ref={ref}
              size='medium'
              fullWidth
            />

            {!fieldState.error ? (
              <InputHintRow>
                <IconInfoCircle size={16} aria-hidden />
                <InputHintText>{i18n('checkout_auth_signup_postal_code_hint')}</InputHintText>
              </InputHintRow>
            ) : null}
          </ZipCodeInputWrapper>
        )}
      />
    );
  }

  return (
    <>
      <FieldWithLinkStack>
        <Controller
          name='zipCode'
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState }) => (
            <MaskedField
              mask='cep'
              name='zipCode'
              label={i18n('checkout_auth_signup_zip_code_label')}
              value={value ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              disabled={disabled}
              required
              fullWidth
            />
          )}
        />

        <Link component='button' type='button' underline='always' onClick={() => setHelpOpen(true)} sx={authLinkSx}>
          {i18n('checkout_auth_signup_zip_code_help_link_label')}
        </Link>
      </FieldWithLinkStack>

      <ZipCodeHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default AddressFields;
